import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
    getTransactionDate,
    validateTransactionForExport,
    transformTransactionForExcel,
    transformTransactionForPDF,
    formatAmount,
    generateFileName,
    validateTransactionBatch,
    showValidationResults
} from './exportUtils';

const ExportTransactionModal = ({ isOpen, onClose, transactions, dossierName = "Dossier" }) => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [type, setType] = useState('all'); // 'all', 'entree', 'sortie', 'rapprochement'
    const [partnerFilter, setPartnerFilter] = useState('all'); // 'all', 'clients', 'suppliers', or specific partner name

    // Extraire la liste unique de tous les partenaires
    const uniquePartners = React.useMemo(() => {
        if (!transactions || !Array.isArray(transactions)) return [];

        const partners = new Set();
        transactions.forEach(t => {
            const partnerName = t.fournisseurs_details?.nom
                || t.fournisseur_nom
                || t.fournisseurs
                || t.client_details?.nom
                || t.client_nom;

            if (partnerName) {
                partners.add(partnerName);
            }
        });

        return Array.from(partners).sort();
    }, [transactions]);

    /**
     * Filtre les transactions selon les critères sélectionnés
     * Utilise maintenant getTransactionDate() pour une gestion robuste des dates
     */
    const filterTransactions = () => {
        if (!transactions || !Array.isArray(transactions)) return [];

        return transactions.filter(t => {
            // Utiliser la fonction utilitaire pour obtenir la date
            const tDate = getTransactionDate(t);

            // Exclure si pas de date valide
            if (!tDate) {
                return false;
            }

            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;

            // Vérifier que les dates de filtre sont valides
            if (start && isNaN(start.getTime())) return false;
            if (end && isNaN(end.getTime())) return false;

            // Ajuster les heures pour inclure toute la journée
            if (end) end.setHours(23, 59, 59, 999);
            if (start) start.setHours(0, 0, 0, 0);

            const matchDate = (!start || tDate >= start) && (!end || tDate <= end);
            const matchType = type === 'all' || t.type === type;

            // Filtre par partenaire
            let matchPartner = true;
            if (partnerFilter !== 'all') {
                const partnerName = t.fournisseurs_details?.nom
                    || t.fournisseur_nom
                    || t.fournisseurs
                    || t.client_details?.nom
                    || t.client_nom;

                if (partnerFilter === 'clients') {
                    // Seulement les transactions avec clients (entrées)
                    matchPartner = t.type === 'entree' && !!(t.client_details?.nom || t.client_nom);
                } else if (partnerFilter === 'suppliers') {
                    // Seulement les transactions avec fournisseurs (sorties)
                    matchPartner = t.type === 'sortie' && !!(t.fournisseurs_details?.nom || t.fournisseur_nom || t.fournisseurs);
                } else {
                    // Un partenaire spécifique
                    matchPartner = partnerName === partnerFilter;
                }
            }

            return matchDate && matchType && matchPartner;
        });
    };

    /**
     * Export Excel avec validation et colonnes enrichies
     */
    const handleExportExcel = () => {
        const data = filterTransactions();
        if (data.length === 0) {
            toast.warn("Aucune transaction à exporter pour cette sélection.");
            return;
        }

        // Validation des données
        const validationStats = validateTransactionBatch(data);

        // Afficher les résultats de validation et demander confirmation
        const userConfirmed = showValidationResults(validationStats);
        if (!userConfirmed) {
            return;
        }

        // Filtrer pour ne garder que les transactions valides
        const validTransactions = data.filter(t => {
            const validation = validateTransactionForExport(t);
            return validation.isValid;
        });

        if (validTransactions.length === 0) {
            toast.error("Aucune transaction valide à exporter.");
            return;
        }

        try {
            // Transformer les données avec les nouvelles fonctions utilitaires
            const excelData = validTransactions.map(transformTransactionForExcel);

            // Créer le workbook
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Ajouter l'onglet principal
            XLSX.utils.book_append_sheet(wb, ws, "Transactions");

            // Calculer les totaux pour l'onglet résumé
            let totalEntrees = 0;
            let totalSorties = 0;
            validTransactions.forEach(t => {
                const amount = parseFloat(t.montant_total || t.montant) || 0;
                if (t.type === 'entree') {
                    totalEntrees += amount;
                } else if (t.type === 'sortie') {
                    totalSorties += amount;
                }
            });
            const solde = totalEntrees - totalSorties;

            // Créer l'onglet résumé
            const summaryData = [
                { 'Indicateur': 'Total des Entrées', 'Valeur': totalEntrees, 'Devise': 'FCFA' },
                { 'Indicateur': 'Total des Sorties', 'Valeur': totalSorties, 'Devise': 'FCFA' },
                { 'Indicateur': 'Solde Net', 'Valeur': solde, 'Devise': 'FCFA' },
                { 'Indicateur': 'Nombre de Transactions', 'Valeur': validTransactions.length, 'Devise': '-' },
                { 'Indicateur': 'Période', 'Valeur': startDate && endDate ? `${startDate} au ${endDate}` : 'Toutes périodes', 'Devise': '-' }
            ];
            const wsSummary = XLSX.utils.json_to_sheet(summaryData);
            XLSX.utils.book_append_sheet(wb, wsSummary, "Résumé");

            // Générer un nom de fichier informatif
            const fileName = generateFileName(
                dossierName,
                { dateStart: startDate, dateEnd: endDate, type },
                'xlsx',
                validTransactions.length
            );

            // Télécharger le fichier
            XLSX.writeFile(wb, fileName);

            toast.success(`Export Excel réussi ! ${validTransactions.length} transaction(s) exportée(s).`);
            onClose();
        } catch (error) {
            console.error('[Export Excel] Erreur:', error);
            toast.error(`Erreur lors de l'export Excel : ${error.message}`);
        }
    };

    /**
     * Export PDF avec validation et métadonnées enrichies
     */
    const handleExportPDF = async () => {
        try {
            const data = filterTransactions();
            if (data.length === 0) {
                toast.warn("Aucune transaction à exporter pour cette sélection.");
                return;
            }

            // Validation des données
            const validationStats = validateTransactionBatch(data);

            // Afficher les résultats de validation et demander confirmation
            const userConfirmed = showValidationResults(validationStats);
            if (!userConfirmed) {
                return;
            }

            // Filtrer pour ne garder que les transactions valides
            const validTransactions = data.filter(t => {
                const validation = validateTransactionForExport(t);
                return validation.isValid;
            });

            if (validTransactions.length === 0) {
                toast.error("Aucune transaction valide à exporter.");
                return;
            }

            // Charger le logo en base64 avant de créer le PDF
            let logoBase64 = null;
            try {
                const logoUrl = window.location.origin + '/logo.png';
                const response = await fetch(logoUrl);
                if (response.ok) {
                    const blob = await response.blob();
                    logoBase64 = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                }
            } catch (e) {
                console.log('Logo non disponible, continuation sans logo:', e);
            }

            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            // En-tête amélioré avec logo et informations
            let headerY = 15;

            // Ajouter le logo si disponible avec un meilleur design - CENTRÉ
            if (logoBase64) {
                try {
                    const logoWidth = 36;
                    const logoHeight = 12;
                    const logoX = (pageWidth - logoWidth) / 2; // Centrer horizontalement
                    const logoY = 14;
                    const rectWidth = 40;
                    const rectHeight = 16;
                    const rectX = (pageWidth - rectWidth) / 2; // Centrer le rectangle aussi
                    const rectY = 12;

                    // Dessiner un rectangle arrondi subtil autour du logo (centré)
                    doc.setDrawColor(44, 95, 45); // Vert EasyCompta
                    doc.setLineWidth(0.5);
                    doc.roundedRect(rectX, rectY, rectWidth, rectHeight, 2, 2, 'S'); // Rectangle centré avec coins arrondis

                    // Ajouter le logo centré dans le rectangle
                    doc.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight);
                } catch (e) {
                    console.log('Erreur lors de l\'ajout du logo:', e);
                }
            }

            headerY = 35; // Ajuster position après le logo

            // Titre principal
            doc.setFontSize(16);
            doc.setTextColor(44, 95, 45); // Vert EasyCompta
            doc.setFont('helvetica', 'bold');
            doc.text('RELEVÉ DE TRANSACTIONS', pageWidth / 2, headerY, { align: 'center' });

            headerY += 10;
            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            doc.setFont('helvetica', 'normal');
            doc.text(`Dossier: ${dossierName}`, pageWidth / 2, headerY, { align: 'center' });

            headerY += 8;

            // Ligne de séparation sous l'en-tête
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.3);
            doc.line(14, headerY, pageWidth - 14, headerY);

            headerY += 7;
            doc.setFontSize(9);

            // Informations de période et type
            let periodText = 'Période : ';
            if (startDate) {
                const start = new Date(startDate);
                periodText += !isNaN(start.getTime()) ? start.toLocaleDateString('fr-FR') : 'Début';
            } else {
                periodText += 'Début';
            }
            periodText += ' au ';
            if (endDate) {
                const end = new Date(endDate);
                periodText += !isNaN(end.getTime()) ? end.toLocaleDateString('fr-FR') : 'Fin';
            } else {
                periodText += 'Fin';
            }
            doc.text(periodText, 14, headerY);

            headerY += 5;
            let typeText = 'Type : ';
            if (type === 'all') {
                typeText += 'Tout';
            } else if (type === 'entree') {
                typeText += 'Entrées';
            } else if (type === 'sortie') {
                typeText += 'Sorties';
            } else if (type === 'rapprochement') {
                typeText += 'Rapprochement';
            }
            doc.text(typeText, 14, headerY);

            headerY += 5;
            doc.text(`Nombre de transactions : ${validTransactions.length}`, 14, headerY);

            headerY += 5;
            const now = new Date();
            doc.text(`Généré le : ${now.toLocaleDateString('fr-FR')} à ${now.toLocaleTimeString('fr-FR')}`, 14, headerY);

            // Préparation des données du tableau
            const tableColumn = ["Date", "Partenaire", "Type", "Libellé", "Montant", "Source"];
            const tableRows = validTransactions.map(transformTransactionForPDF);

            // Calculer les totaux
            let totalEntrees = 0;
            let totalSorties = 0;
            validTransactions.forEach(t => {
                const amount = parseFloat(t.montant_total || t.montant) || 0;
                if (t.type === 'entree') {
                    totalEntrees += amount;
                } else if (t.type === 'sortie') {
                    totalSorties += amount;
                }
            });
            const solde = totalEntrees - totalSorties;

            // Configuration du tableau
            const tableStartY = headerY + 10;

            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: tableStartY,
                theme: 'grid',
                styles: {
                    fontSize: 7,
                    cellPadding: 2,
                    halign: 'left',
                    valign: 'middle',
                    textColor: [0, 0, 0] // Noir pour meilleure lisibilité
                },
                headStyles: {
                    fillColor: [44, 95, 45],
                    textColor: 255,
                    fontSize: 8,
                    fontStyle: 'bold',
                    halign: 'center',
                    valign: 'middle'
                },
                columnStyles: {
                    0: { cellWidth: 25, halign: 'center' }, // Date
                    1: { cellWidth: 40, halign: 'left' },   // Partenaire
                    2: { cellWidth: 20, halign: 'center' }, // Type
                    3: { cellWidth: 55, halign: 'left' },   // Libellé
                    4: { cellWidth: 30, halign: 'right' },  // Montant
                    5: { cellWidth: 20, halign: 'center' }  // Source
                },
                margin: { top: tableStartY, left: 14, right: 14, bottom: 50 },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                didDrawPage: (data) => {
                    // Footer simplifié : seulement "Page X" à droite
                    doc.setFontSize(9);
                    doc.setTextColor(128, 128, 128);
                    doc.text(`Page ${data.pageNumber}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
                }
            });

            // R résumé toujours en bas de la dernière page
            const finalY = doc.lastAutoTable.finalY || tableStartY + (tableRows.length * 5);

            // Vérifier si on a assez d'espace pour le résumé (40mm nécessaire)
            const spaceNeeded = 40;
            const spaceAvailable = pageHeight - finalY - 20; // -20 pour footer

            if (spaceAvailable < spaceNeeded) {
                // Ajouter une nouvelle page si pas assez d'espace
                doc.addPage();
            }

            // Positionner le résumé en bas de page (40mm avant la fin)
            let footerY = pageHeight - 50;

            // Ligne de séparation
            doc.setDrawColor(200, 200, 200);
            doc.line(14, footerY, pageWidth - 14, footerY);
            footerY += 8;

            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.setFont('helvetica', 'bold');

            // Totaux
            doc.text('RÉSUMÉ', 14, footerY);
            footerY += 7;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(`Total des entrées :`, 20, footerY);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(44, 95, 45);
            doc.text(formatAmount(totalEntrees), pageWidth - 30, footerY, { align: 'right' });

            footerY += 6;
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 0);
            doc.text(`Total des sorties :`, 20, footerY);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(191, 27, 27);
            doc.text(formatAmount(totalSorties), pageWidth - 30, footerY, { align: 'right' });

            footerY += 6;
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 0);
            doc.text(`Solde net :`, 20, footerY);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(solde >= 0 ? 44 : 191, solde >= 0 ? 95 : 27, solde >= 0 ? 45 : 27);
            doc.text(formatAmount(solde), pageWidth - 30, footerY, { align: 'right' });

            footerY += 10;
            doc.setDrawColor(200, 200, 200);
            doc.line(14, footerY, pageWidth - 14, footerY);

            footerY += 8;
            doc.setFontSize(9);
            doc.setTextColor(120, 120, 120);
            doc.setFont('helvetica', 'italic');
            doc.text('Généré avec EasyCompta', pageWidth / 2, footerY, { align: 'center' });

            // Générer un nom de fichier informatif
            const fileName = generateFileName(
                dossierName,
                { dateStart: startDate, dateEnd: endDate, type },
                'pdf',
                validTransactions.length
            );

            doc.save(fileName);
            toast.success(`Export PDF réussi ! ${validTransactions.length} transaction(s) exportée(s).`);
            onClose();
        } catch (error) {
            console.error('[Export PDF] Erreur:', error);
            toast.error(`Erreur lors de l'export PDF : ${error.message}`);
        }
    };

    return (
        <Modal show={isOpen} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Exporter les Transactions</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Période</Form.Label>
                        <Row>
                            <Col>
                                <Form.Control
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    placeholder="Du"
                                />
                            </Col>
                            <Col>
                                <Form.Control
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    placeholder="Au"
                                />
                            </Col>
                        </Row>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Type de Transaction</Form.Label>
                        <Form.Select value={type} onChange={(e) => setType(e.target.value)}>
                            <option value="all">Tout (Entrées, Sorties et Rapprochement)</option>
                            <option value="entree">Entrées Seulement</option>
                            <option value="sortie">Sorties Seulement</option>
                            <option value="rapprochement">Rapprochement Seulement</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Partenaire</Form.Label>
                        <Form.Select value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)}>
                            <option value="all">Tous les partenaires</option>
                            <option value="clients">Clients uniquement</option>
                            <option value="suppliers">Fournisseurs uniquement</option>
                            {uniquePartners.length > 0 && <option disabled>──────────</option>}
                            {uniquePartners.map(partner => (
                                <option key={partner} value={partner}>{partner}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>Annuler</Button>
                <Button variant="success" onClick={handleExportExcel}>Excel</Button>
                <Button variant="danger" onClick={handleExportPDF}>PDF</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ExportTransactionModal;
